import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { LayoutTemplate, Plus, Trash2, Pencil, X, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const EMPTY_ITEM = { description: '', quantity: 1, unit_price: '' };
const eur = (v) => Number(v || 0).toFixed(2).replace('.', ',') + ' €';

// Manage reusable offer templates (create / edit / delete).
export default function OfferTemplatesDialog({ open, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, title, items[], note} | null
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/offer-templates').then((r) => setTemplates(r.data?.templates || [])).catch(() => {});
  }, []);
  useEffect(() => { if (open) { load(); setEditing(null); } }, [open, load]);

  const startNew = () => setEditing({ title: '', items: [{ ...EMPTY_ITEM }], note: '' });
  const startEdit = (t) => setEditing({
    id: t.id, title: t.title,
    items: (t.items || []).map((i) => ({ description: i.description || '', quantity: i.quantity ?? 1, unit_price: i.unit_price ?? '' })),
    note: t.note || '',
  });

  const total = editing ? editing.items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0) : 0;
  const setItems = (fn) => setEditing((e) => ({ ...e, items: fn(e.items) }));
  const updateItem = (idx, field, val) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));

  const save = async () => {
    if (!editing.title.trim()) { toast.error('Bitte einen Titel angeben.'); return; }
    const clean = editing.items.filter((i) => (i.description || '').trim())
      .map((i) => ({ description: i.description.trim(), quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0 }));
    if (!clean.length) { toast.error('Bitte mindestens eine Position.'); return; }
    setBusy(true);
    try {
      await api.post('/offer-templates', { id: editing.id || null, title: editing.title.trim(), items: clean, note: editing.note });
      toast.success('Vorlage gespeichert');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Diese Vorlage löschen?')) return;
    try { await api.delete(`/offer-templates/${id}`); toast.success('Vorlage gelöscht'); load(); }
    catch { toast.error('Fehler beim Löschen'); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="offer-templates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <LayoutTemplate className="w-5 h-5" /> Angebot-Vorlagen
          </DialogTitle>
        </DialogHeader>

        {!editing ? (
          <div className="space-y-3">
            {templates.length === 0 && (
              <p className="text-sm text-[#9CA3AF] py-6 text-center" data-testid="templates-empty">Noch keine Vorlagen. Legen Sie Ihre erste an.</p>
            )}
            <div className="space-y-2" data-testid="templates-list">
              {templates.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#E5E5E0]" data-testid={`template-row-${t.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-[#1C1C19] truncate">{t.title}</div>
                    <div className="text-xs text-[#9CA3AF]">{(t.items || []).length} Positionen · {eur(t.total)}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => startEdit(t)} data-testid={`template-edit-${t.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(t.id)} className="text-[#EF4444]" data-testid={`template-delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={startNew} className="w-full bg-[#1C1C19] hover:bg-[#3A3A35]" data-testid="template-new-btn">
              <Plus className="w-4 h-4 mr-1" /> Neue Vorlage
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">Titel</Label>
              <Input value={editing.title} onChange={(e) => setEditing((s) => ({ ...s, title: e.target.value }))} placeholder="z. B. Weihnachtsfeier-Paket" className="mt-1" data-testid="template-title" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">Positionen</Label>
              <div className="space-y-2 mt-2">
                {editing.items.map((it, idx) => (
                  <div key={idx} className="flex items-start gap-2" data-testid={`template-item-${idx}`}>
                    <Input value={it.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} placeholder="Beschreibung" className="flex-1" data-testid={`template-item-desc-${idx}`} />
                    <Input type="number" min="0" value={it.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} placeholder="Menge" className="w-16 text-center" data-testid={`template-item-qty-${idx}`} />
                    <Input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} placeholder="€" className="w-24 text-right" data-testid={`template-item-price-${idx}`} />
                    <Button variant="ghost" size="icon" onClick={() => setItems((arr) => (arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr))} className="text-[#EF4444] shrink-0"><X className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={() => setItems((arr) => [...arr, { ...EMPTY_ITEM }])} className="mt-2" data-testid="template-add-item"><Plus className="w-4 h-4 mr-1" /> Position</Button>
            </div>
            <div className="flex items-center justify-between border-t border-[#E5E5E0] pt-3">
              <span className="text-sm font-medium text-[#5C5C59]">Gesamt</span>
              <span className="text-lg font-semibold text-[#1C1C19]">{eur(total)}</span>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">Anschreiben (optional)</Label>
              <Textarea value={editing.note} onChange={(e) => setEditing((s) => ({ ...s, note: e.target.value }))} rows={2} className="mt-1" data-testid="template-note" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Zurück</Button>
              <Button onClick={save} disabled={busy} className="bg-[#1C1C19] hover:bg-[#3A3A35]" data-testid="template-save"><Save className="w-4 h-4 mr-1" /> Speichern</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
